import { useCallback, useEffect, useState } from 'react';

import type { Key } from 'react';
import { Optional } from 'chaika-common-types';
import { isDefined } from '@utils/isDefined';
import { PresetFiltersParams } from '@components/Table/filters/PresetFilters/PresetFilters';

import type { LocalSortState } from '../VirtualTable/useLocalSort';
import type { UrlTableState } from './useUrlTableState';

interface Props {
    selectedFilters?: Record<any, any>;
    presetFiltersParams?: PresetFiltersParams;
    onFiltersChange?(params: Record<any, any>): Promise<void> | void;
    readUrlState(): UrlTableState;
    hydrateSearchFromUrl(): Promise<void> | void;
    hydrateSortFromUrl(): Promise<void> | void;
    writePresetToUrl(presetIndex: number, sort?: LocalSortState): void;
    applyPresetSort(sort: LocalSortState | null): void;
    clearUrlFilters(): void;
}

interface Data {
    tableIndex: number;
    selectedPreset?: number;
    hasFilters: boolean;
    hasPresetFilters: boolean;
    buildAllFilters(overrideKey: string, overrideValue: Key[] | null): Record<string, Key[] | null>;
    onResetFilters(): void;
    onPresetFilterClick(params: Record<any, any>, index?: number): Promise<void> | void;
}

export function useFilters({
    selectedFilters,
    presetFiltersParams,
    onFiltersChange,
    readUrlState,
    hydrateSearchFromUrl,
    hydrateSortFromUrl,
    writePresetToUrl,
    applyPresetSort,
    clearUrlFilters,
}: Props): Data {
    const [tableIndex, setTableIndex] = useState(0);
    const [selectedPreset, setSelectedPreset] = useState<Optional<number>>();

    useEffect(() => {
        void hydrateFromUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasFilters = !!selectedFilters && Object.values(selectedFilters).some(value => !!value);
    const hasPresetFilters = !!presetFiltersParams;

    // Строго последовательно: сортировка собирает запрос из параметров, которые страница
    // выставляет только по ответу пресета — параллельный старт даёт запрос без фильтров.
    async function hydrateFromUrl(): Promise<void> {
        await hydrateSearchFromUrl();
        await hydratePresetAndFilters();
        await hydrateSortFromUrl();
    }

    // URL хранит индекс пресета, а не его значения: сами значения берём из кода, иначе скаляры
    // вроде `sortingOrder` вернулись бы из URL массивами.
    async function hydratePresetAndFilters(): Promise<void> {
        const { presetIndex, columnFilters } = readUrlState();
        const presetFromUrl = isDefined(presetIndex) ? presetFiltersParams?.presetFilters[presetIndex] : undefined;

        if (presetFromUrl) {
            await applyPresetFilters({ ...presetFromUrl.filters, ...columnFilters }, presetIndex);

            return;
        }

        if (Object.keys(columnFilters).length > 0) {
            await onFiltersChange?.(columnFilters);

            return;
        }

        const defaultPresetIndex = presetFiltersParams?.presetFilters.findIndex(it => it.isDefaultPreset) ?? -1;
        const defaultPreset = presetFiltersParams?.presetFilters[defaultPresetIndex];

        if (defaultPreset) {
            await applyPresetFilters(defaultPreset.filters, defaultPresetIndex);
        }
    }

    async function onPresetFilterClick(params: Record<any, any>, index?: number): Promise<void> {
        if (isDefined(index)) {
            writePresetToUrl(index, readPresetSort(index));
        }

        await applyPresetFilters(params, index);
    }

    // Сортировку выставляем до запроса: при гидрации её следом перетрёт `hydrateSortFromUrl`, если URL несёт свою.
    async function applyPresetFilters(params: Record<any, any>, index?: number): Promise<void> {
        if (!presetFiltersParams?.onPresetFilter) {
            return;
        }

        setTableIndex(prev => prev + 1);

        if (presetFiltersParams.presetByIndex) {
            setSelectedPreset(index);
        }

        applyPresetSort(readPresetSort(index) ?? null);

        await presetFiltersParams.onPresetFilter(params);
    }

    function readPresetSort(index?: number): Optional<LocalSortState> {
        return isDefined(index) ? presetFiltersParams?.presetFilters[index]?.sort : undefined;
    }

    function onResetFilters(): void {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isDefined(selectedPreset) && setSelectedPreset(undefined);
        clearUrlFilters();
        onFiltersChange?.({});
        setTableIndex(prev => prev + 1);
    }

    const buildAllFilters = useCallback(
        (overrideKey: string, overrideValue: Key[] | null): Record<string, Key[] | null> => ({
            ...(selectedFilters ?? {}),
            [overrideKey]: overrideValue,
        }),
        [selectedFilters],
    );

    return {
        tableIndex,
        hasFilters,
        hasPresetFilters,
        selectedPreset,
        buildAllFilters,
        onResetFilters,
        onPresetFilterClick,
    };
}
